import { type ColumnDef, type Table as RTTable, getCoreRowModel, getSortedRowModel, useReactTable, flexRender, type SortingState } from '@tanstack/react-table'
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, MoreHorizontal, StickyNote } from 'lucide-react'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'
import { useDeletePO, useDuplicatePO, useUnarchivePO, useReopenPO, useRevertPOToDraft, useRetrySync } from '@/hooks/usePOMutations'
import type { components } from '@/lib/api/generated'
import { cn } from '@/lib/utils'
import { fmtDate } from '@/lib/dates'
import { useUIStore } from '@/store/uiStore'
import { ColumnSettings } from './ColumnSettings'
import { POExpandedDetail } from './POExpandedDetail'
import { NotSyncedPill } from './NotSyncedPill'
import { StatusBadge } from './StatusBadge'

type POListItem = components['schemas']['POListItem']

// unsynced_lines isn't in generated.ts until the next gen-api regen — same
// narrow cast as has_tracking below. Lines whose Shopify push failed or was
// skipped (sync off / offline / no credentials).
function unsyncedLines(po: POListItem): number {
  return (po as { unsynced_lines?: number }).unsynced_lines ?? 0
}

const ExpandContext = React.createContext<{
  expanded: Set<string>
  toggle: (id: string) => void
} | null>(null)

function useExpandContext() {
  const ctx = React.useContext(ExpandContext)
  if (!ctx) throw new Error('ExpandContext missing')
  return ctx
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(val)
}

// Strip any leading '#' characters so we never double-prefix (e.g. stored
// po_number "#3542" + our own "#" prefix would show "##3542").
function displayPONumber(raw: string | null | undefined): string {
  if (!raw) return '—'
  const stripped = raw.replace(/^#+/, '')
  return stripped ? `#${stripped}` : '—'
}

function PONumberCell({ po }: { po: POListItem }) {
  const { setActiveModal } = useUIStore()
  const navigate = useNavigate()
  const label = displayPONumber(po.po_number || po.po_id)
  const status = po.status
  function handleClick() {
    // ORDERED / PARTIAL_ORDERED jump straight to the receiving page — the
    // modal would just be a read-only view at that stage, and clicking the
    // PO# is the user's primary "I want to work on this PO now" gesture.
    if (status === 'ORDERED' || status === 'PARTIAL_ORDERED') {
      navigate('/purchase-orders/' + encodeURIComponent(po.po_id) + '/receive')
      return
    }
    // Everything else opens in the wizard modal. EditPOModal picks the
    // right primary-action button (Submit / Approve / Mark Ordered) based
    // on the loaded status.
    setActiveModal('edit', po.po_id)
  }
  // approval_notes / notes aren't in generated.ts's POListItem until the next
  // gen-api regen — same narrow cast the has_tracking flag uses below.
  const { approval_notes, notes } = po as {
    approval_notes?: string | null
    notes?: string | null
  }
  const noteText = [notes, approval_notes]
    .map((t) => (t ?? '').trim())
    .filter(Boolean)
    .join('\n')
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        className="font-medium text-[var(--brand)] text-xs tabular-nums hover:underline cursor-pointer"
        onClick={handleClick}
        aria-label={
          status === 'ORDERED' || status === 'PARTIAL_ORDERED'
            ? `Receive PO ${label}`
            : `View PO ${label}`
        }
      >
        {label}
      </button>
      {noteText && (
        <span title={noteText} aria-label={`PO ${label} has notes`}>
          <StickyNote className="h-3 w-3 text-[var(--mute)]" />
        </span>
      )}
    </span>
  )
}

// A shipment's ETA beats the buyer's estimate at PO creation — one comes from
// a parcel that actually exists. When several shipments have dates the backend
// sends the LATEST, since the PO isn't complete until the last one lands.
// Linked to that shipment's carrier page; plain text when the PO has no
// tracking, or when the carrier has no tracking URL (e.g. "Other").
function ExpectedDeliveryCell({ po }: { po: POListItem }) {
  // Not in generated.ts until the next gen-api regen — same narrow cast the
  // has_tracking flag uses below.
  const { tracking_expected_delivery: fromShipment, tracking_url: trackingUrl } =
    po as {
      tracking_expected_delivery?: string | null
      tracking_url?: string | null
    }
  const date = fromShipment ?? po.expected_delivery_date
  const label = fmtDate(date, '—')

  // Link whenever the PO is trackable — NOT only when a shipment carries an
  // ETA. A PO can have a working tracking link and no promised date (Arana
  // dispatch tracking never has one), and requiring the ETA left those rows
  // as plain text even though the tracking modal linked them fine.
  // Still plain text when there's no date at all: a bare "—" as a link reads
  // as a mistake rather than an affordance.
  if (!trackingUrl || !date) {
    return (
      <span className="text-xs tabular-nums whitespace-nowrap text-[var(--mute)]">{label}</span>
    )
  }
  return (
    <a
      href={trackingUrl}
      target="_blank"
      rel="noopener noreferrer"
      // The row has its own click handlers (expand / open); without this the
      // link would also toggle the row underneath it.
      onClick={(e) => e.stopPropagation()}
      className="text-xs tabular-nums whitespace-nowrap text-[var(--brand)] hover:underline"
      title="Track this shipment"
    >
      {label}
    </a>
  )
}

function ContextMenu({ po }: { po: POListItem }) {
  const { setActiveModal } = useUIStore()
  const navigate = useNavigate()
  const deleteMutation = useDeletePO()
  const duplicateMutation = useDuplicatePO()
  const unarchiveMutation = useUnarchivePO()
  const reopenMutation = useReopenPO()
  const revertMutation = useRevertPOToDraft()
  const retryMutation = useRetrySync()
  const status = po.status
  // Retry Sync is open to every user (receiving staff spot the misses).
  // PARTIAL_ORDERED keeps it (that status means a push already failed);
  // otherwise only when lines are owed.
  const canRetrySync = unsyncedLines(po) > 0 || status === 'PARTIAL_ORDERED'
  // has_tracking isn't in generated.ts until the next gen-api regen (the
  // generator needs a running backend). Narrow cast, same workaround as
  // VendorUpsertRequest in useVendorMutations.ts.
  const hasTracking = (po as { has_tracking?: boolean }).has_tracking ?? false

  function handleDelete() {
    const label = po.po_number || po.po_id
    toast(`Delete draft PO ${label}?`, {
      action: {
        label: 'Delete',
        onClick: () => {
          deleteMutation.mutate(po.po_id, {
            onSuccess: () => toast.success(`Deleted ${label}`),
            onError: (err) =>
              toast.error(err instanceof Error ? err.message : 'Failed to delete PO'),
          })
        },
      },
    })
  }

  function handleDuplicate() {
    const label = po.po_number || po.po_id
    duplicateMutation.mutate(po.po_id, {
      onSuccess: (newPo) => {
        toast.success(`Duplicated ${label} → new draft ${newPo.po_id}`)
        setActiveModal('edit', newPo.po_id)
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to duplicate PO'),
    })
  }

  function handleUnarchive() {
    const label = po.po_number || po.po_id
    toast(`Unarchive PO ${label}? Shopify INCOMING will be restored.`, {
      action: {
        label: 'Unarchive',
        onClick: () => {
          unarchiveMutation.mutate(po.po_id, {
            onSuccess: () => toast.success(`Unarchived ${label} → ORDERED`),
            onError: (err) =>
              toast.error(err instanceof Error ? err.message : 'Failed to unarchive PO'),
          })
        },
      },
    })
  }

  function handleRetrySync() {
    const label = po.po_number || po.po_id
    retryMutation.mutate(po.po_id, {
      onSuccess: (res) => {
        if (res.skipped) {
          toast.warning(
            'Shopify sync is still off — nothing was sent. Turn it on in Admin, then retry.'
          )
          return
        }
        const sync = (res.sync ?? {}) as { synced?: unknown[]; failed?: unknown[] }
        const n = sync.synced?.length ?? 0
        const failed = sync.failed?.length ?? 0
        const msg = `Synced ${n} line${n === 1 ? '' : 's'} to Shopify`
        if (failed > 0) toast.warning(`${msg} · ${failed} still failed on ${label}`)
        else toast.success(msg)
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Retry sync failed'),
    })
  }

  function handleRevert() {
    const label = po.po_number || po.po_id
    revertMutation.mutate(po.po_id, {
      onSuccess: () => {
        toast.success(`Reverted ${label} → DRAFT`)
        setActiveModal('edit', po.po_id)
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to revert PO'),
    })
  }

  function handleReopen() {
    const label = po.po_number || po.po_id
    reopenMutation.mutate(po.po_id, {
      onSuccess: () => {
        toast.success(`Reopened ${label} → DRAFT. Edit and resubmit.`)
        setActiveModal('edit', po.po_id)
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to reopen PO'),
    })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 p-0 text-[var(--mute)] hover:text-[var(--text-primary)]"
          aria-label="Row actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={() => setActiveModal('edit', po.po_id)}>
          View
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={handleDuplicate}
          disabled={duplicateMutation.isPending}
        >
          Duplicate
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={() => setActiveModal('tracking', po.po_id)}>
          {hasTracking ? 'View Tracking' : 'Add Tracking'}
        </DropdownMenuItem>

        {status === 'ARCHIVED' && (
          <DropdownMenuItem
            onSelect={handleUnarchive}
            disabled={unarchiveMutation.isPending}
          >
            Unarchive
          </DropdownMenuItem>
        )}

        {status === 'DRAFT' && (
          <>
            <DropdownMenuItem
              onSelect={() => setActiveModal('edit', po.po_id)}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onSelect={handleDelete}
              disabled={deleteMutation.isPending}
            >
              Delete
            </DropdownMenuItem>
          </>
        )}

        {status === 'AWAITING_APPROVAL' && (
          <DropdownMenuItem onSelect={() => setActiveModal('approval', po.po_id)}>
            Approve / Reject
          </DropdownMenuItem>
        )}

        {status === 'REJECTED' && (
          <DropdownMenuItem
            onSelect={handleReopen}
            disabled={reopenMutation.isPending}
          >
            Reopen to draft
          </DropdownMenuItem>
        )}

        {status === 'AWAITING_APPROVAL' && (
          <DropdownMenuItem
            onSelect={handleRevert}
            disabled={revertMutation.isPending}
          >
            Revert to draft
          </DropdownMenuItem>
        )}

        {status === 'APPROVED' && (
          po.is_kentucky ? (
            // Kentucky POs get both: the primary transmit action AND a plain
            // Mark Ordered escape hatch (books Shopify incoming without sending
            // the order to Kentucky — e.g. a test PO already placed elsewhere).
            <>
              <DropdownMenuItem onSelect={() => setActiveModal('orderKentucky', po.po_id)}>
                Order with Kentucky
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setActiveModal('markOrdered', po.po_id)}>
                Mark ordered (don’t send to Kentucky)
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem onSelect={() => setActiveModal('markOrdered', po.po_id)}>
              Mark Ordered
            </DropdownMenuItem>
          )
        )}

        {(status === 'ORDERED' || status === 'PARTIAL_ORDERED') && (
          <>
            <DropdownMenuItem
              onSelect={() =>
                navigate(
                  '/purchase-orders/' + encodeURIComponent(po.po_id) + '/receive'
                )
              }
            >
              Receive Items
            </DropdownMenuItem>
          </>
        )}

        {canRetrySync && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={handleRetrySync}
              disabled={retryMutation.isPending}
            >
              Retry Sync
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ExpandToggleCell({ poId }: { poId: string }) {
  const { expanded, toggle } = useExpandContext()
  const isOpen = expanded.has(poId)
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 p-0 text-[var(--mute)] hover:text-[var(--text-primary)]"
      onClick={() => toggle(poId)}
      aria-label={isOpen ? 'Hide items' : 'View items'}
      aria-expanded={isOpen}
    >
      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
    </Button>
  )
}

function SelectAllHeader({ table }: { table: RTTable<POListItem> }) {
  const { selectedPOs, toggleSelectAll } = useUIStore()
  const allIds = table.getRowModel().rows.map((r) => r.original.po_id)
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedPOs.has(id))
  const someSelected = allIds.some((id) => selectedPOs.has(id))
  return (
    <input
      type="checkbox"
      checked={allSelected}
      ref={(el) => {
        if (el) el.indeterminate = someSelected && !allSelected
      }}
      onChange={() => toggleSelectAll(allIds)}
      className="h-3.5 w-3.5 cursor-pointer"
      aria-label="Select all"
    />
  )
}

function RowSelectCell({ id }: { id: string }) {
  const { selectedPOs, toggleSelectedPO } = useUIStore()
  return (
    <input
      type="checkbox"
      checked={selectedPOs.has(id)}
      onChange={() => toggleSelectedPO(id)}
      className="h-3.5 w-3.5 cursor-pointer"
      aria-label={`Select PO ${id}`}
    />
  )
}

// Paid checkbox is UI-only until POListItem grows a `paid` boolean and the
// backend exposes a /payments endpoint. Local state lets you toggle within
// a session for demo/visual feedback; refresh resets to unchecked.
// TODO: persist via PATCH /api/purchase-orders/{po_id}/paid once available.
function PaidCheckbox({ poId }: { poId: string }) {
  const [paid, setPaid] = React.useState(false)
  return (
    <input
      type="checkbox"
      checked={paid}
      onChange={(e) => setPaid(e.target.checked)}
      className="h-3.5 w-3.5 cursor-pointer"
      aria-label={`Paid ${poId}`}
    />
  )
}

const PLACEHOLDER = (
  <span className="text-xs text-[var(--mute)]">—</span>
)

// Column order matches the user's spec (May 15):
//   PO # | Vendor | [Supplier] | [Invoice #] | [Supp. order #] |
//   Generated | [Ordered] | Shipping | Items | Status |
//   [Ship date] | Expected | Total cost | Received |
//   [Payment status] | [Payment due] | ⋯
// Columns in brackets are off by default; ColumnSettings flips them on.
// Cells marked "no backend data yet" render `—` until POListItem grows.
const COLUMNS: ColumnDef<POListItem>[] = [
  {
    id: 'select',
    header: ({ table }) => <SelectAllHeader table={table} />,
    cell: ({ row }) => <RowSelectCell id={row.original.po_id} />,
    enableSorting: false,
    size: 32,
  },
  {
    id: 'expand',
    header: '',
    cell: ({ row }) => <ExpandToggleCell poId={row.original.po_id} />,
    enableSorting: false,
    size: 28,
  },
  {
    accessorKey: 'po_number',
    id: 'po_number',
    header: 'PO #',
    cell: ({ row }) => <PONumberCell po={row.original} />,
    enableSorting: true,
  },
  {
    accessorKey: 'vendor',
    id: 'vendor',
    header: 'Vendor',
    cell: ({ row }) => {
      // The vendor is the brand; the supplier is who supplies it. They are
      // distinct and each owns its own column — this cell shows the vendor
      // only. (It previously led with the supplier and demoted the vendor to
      // a secondary line, which made a supplier-centric PO read as though the
      // supplier were the vendor.) Supplier falls back in only when the PO
      // carries no vendor at all, so the cell is never blank.
      const supplier = (row.original.supplier ?? '').trim()
      const vendor = (row.original.vendor ?? '').trim()
      if (!supplier && !vendor) {
        return <span className="text-xs text-[var(--text-primary)]">—</span>
      }
      return (
        <span className="text-xs text-[var(--text-primary)]">{vendor || supplier}</span>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'supplier',
    id: 'supplier',
    header: 'Supplier',
    cell: ({ row }) => (
      <span className="text-xs text-[var(--mute)]">
        {row.original.supplier || '—'}
      </span>
    ),
    enableSorting: false,
  },
  {
    id: 'invoice_number',
    header: 'Invoice #',
    cell: () => PLACEHOLDER,
    enableSorting: false,
  },
  {
    id: 'supplier_order_number',
    header: 'Supp. order #',
    cell: () => PLACEHOLDER,
    enableSorting: false,
  },
  {
    accessorKey: 'created_date',
    id: 'created_date',
    header: 'Generated',
    cell: ({ row }) => {
      const date = fmtDate(row.original.created_date, '—')
      const who = row.original.created_by
      return (
        <span className="text-xs tabular-nums text-[var(--mute)]">
          <span className="whitespace-nowrap">{date}</span>
          {who && date !== '—' ? ` · ${who}` : ''}
        </span>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: 'ordered_date',
    id: 'ordered_date',
    header: 'Ordered',
    cell: ({ row }) => (
      <span className="text-xs tabular-nums whitespace-nowrap text-[var(--mute)]">
        {fmtDate(row.original.ordered_date, '—')}
      </span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'location_to_receive',
    id: 'location_to_receive',
    header: 'Shipping',
    cell: ({ row }) => (
      <span className="text-xs text-[var(--mute)]">
        {row.original.location_to_receive || '—'}
      </span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'items_count',
    id: 'items_count',
    header: 'Items',
    cell: ({ row }) => (
      <span className="text-xs tabular-nums text-[var(--ink-2)]">
        {row.original.items_count ?? '—'}
      </span>
    ),
    enableSorting: false,
    meta: { numeric: true },
  },
  {
    accessorKey: 'status',
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const n = unsyncedLines(row.original)
      return (
        <span className="inline-flex items-center gap-1">
          <StatusBadge status={row.original.status} />
          {n > 0 && (
            <NotSyncedPill title={`${n} line${n === 1 ? '' : 's'} didn't reach Shopify`} />
          )}
        </span>
      )
    },
    enableSorting: false,
  },
  {
    id: 'ship_date',
    header: 'Ship date',
    cell: () => PLACEHOLDER,
    enableSorting: false,
  },
  {
    accessorKey: 'expected_delivery_date',
    id: 'expected_delivery_date',
    header: 'Expected',
    cell: ({ row }) => (
      <ExpectedDeliveryCell po={row.original} />
    ),
    enableSorting: true,
  },
  {
    id: 'payment_status',
    header: 'Paid',
    cell: ({ row }) => (
      <div className="flex justify-center">
        <PaidCheckbox poId={row.original.po_id} />
      </div>
    ),
    enableSorting: false,
  },
  {
    id: 'payment_due_date',
    header: 'Payment due',
    cell: () => PLACEHOLDER,
    enableSorting: false,
  },
  {
    accessorKey: 'total_cost_base',
    id: 'total_cost_base',
    header: 'Total cost',
    cell: ({ row }) => (
      <span className="text-xs tabular-nums font-medium text-[var(--text-primary)]">
        {formatCurrency(row.original.total_cost_base)}
      </span>
    ),
    enableSorting: true,
    meta: { numeric: true },
  },
  {
    id: 'received_count',
    header: 'Received',
    cell: ({ row }) => {
      // Backend now returns aggregated ordered_units / received_units in the
      // PO list response, computed via a single LEFT JOIN per request.
      // Falls back to items_count when ordered_units isn't present (older
      // backend or POs with zero line items).
      const ordered = row.original.ordered_units ?? row.original.items_count ?? 0
      const received = row.original.received_units ?? 0
      if (!ordered) return <span className="text-xs tabular-nums text-[var(--mute)]">—</span>
      // Conventional status colours: green = all in, amber = partly in
      // (needs chasing), plain mute = nothing yet (not a problem on its own).
      // Full/partial get a tinted chip because coloured 12px text alone read
      // as grey on staging. rounded-sm (not rounded-full) so it doesn't read
      // as a status pill.
      if (received <= 0) {
        return (
          <span className="text-xs tabular-nums text-[var(--mute)]">
            {received} of {ordered}
          </span>
        )
      }
      const complete = received >= ordered
      return (
        <span
          className={cn(
            'inline-block rounded-sm px-1.5 py-0.5 leading-none tabular-nums text-xs',
            complete
              ? 'bg-[var(--received-full-bg)] text-[var(--received-full)] font-medium'
              : 'bg-[var(--received-partial-bg)] text-[var(--received-partial)]'
          )}
        >
          {received} of {ordered}
        </span>
      )
    },
    enableSorting: false,
    meta: { numeric: true },
  },
  {
    id: 'actions',
    header: () => <ColumnSettings />,
    cell: ({ row }) => <ContextMenu po={row.original} />,
    enableSorting: false,
    size: 40,
  },
]

interface POTableProps {
  data: POListItem[]
  onClearFilters?: () => void
}

// Columns the user never gets to hide (and that don't appear in the
// gear menu). Anything else is driven by uiStore.columnVisibility.
const ALWAYS_VISIBLE = new Set([
  'select',
  'expand',
  'po_number',
  'vendor',
  'status',
  'items_count',
  'actions',
])

export function POTable({ data, onClearFilters }: POTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set())
  const columnVisibility = useUIStore((s) => s.columnVisibility)

  const toggle = React.useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])
  const expandCtx = React.useMemo(() => ({ expanded, toggle }), [expanded, toggle])

  // Merge persisted toggles with the always-visible set. TanStack treats
  // missing entries as visible, so we only need to write `false` for the
  // toggleable columns that are turned off.
  const effectiveVisibility = React.useMemo(() => {
    const out: Record<string, boolean> = {}
    for (const col of COLUMNS) {
      const id = col.id ?? (col as { accessorKey?: string }).accessorKey
      if (!id || ALWAYS_VISIBLE.has(id)) continue
      out[id] = columnVisibility[id] ?? false
    }
    return out
  }, [columnVisibility])

  const table = useReactTable({
    data,
    columns: COLUMNS,
    state: { sorting, columnVisibility: effectiveVisibility },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-[var(--mute)]">
          No purchase orders match these filters
        </p>
        {onClearFilters && (
          <Button
            variant="outline"
            size="sm"
            className="mt-4 text-xs"
            onClick={onClearFilters}
          >
            Clear filters
          </Button>
        )}
      </div>
    )
  }

  const visibleColCount = table.getVisibleLeafColumns().length

  return (
    <ExpandContext.Provider value={expandCtx}>
    <div className="overflow-auto flex-1">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="hover:bg-transparent border-[var(--line-warm)]">
              {hg.headers.map((header) => {
                const numeric =
                  (header.column.columnDef.meta as { numeric?: boolean } | undefined)
                    ?.numeric ?? false
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'sticky top-0 z-10',
                      numeric && 'text-right',
                      header.column.getCanSort() && 'cursor-pointer select-none'
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <span
                      className={cn(
                        'inline-flex items-center gap-1',
                        numeric && 'justify-end w-full'
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getCanSort() && (
                        <span className="opacity-50">
                          {header.column.getIsSorted() === 'asc' ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronsUpDown className="h-3 w-3" />
                          )}
                        </span>
                      )}
                    </span>
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row, index) => {
            const poId = row.original.po_id
            const isExpanded = expanded.has(poId)
            return (
              <React.Fragment key={row.id}>
                {/* Zebra keyed on the PO row index, not CSS nth-child, so an
                    expanded detail row in between doesn't flip the pattern. */}
                <TableRow
                  className={cn(
                    'border-b border-[var(--line-warm)]',
                    index % 2 === 1
                      ? 'bg-[var(--surface-zebra)]'
                      : 'bg-[var(--surface-card)]'
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const numeric =
                      (cell.column.columnDef.meta as { numeric?: boolean } | undefined)
                        ?.numeric ?? false
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn('py-1.5 px-2 text-xs', numeric && 'text-right tabular-nums')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    )
                  })}
                </TableRow>
                {isExpanded && (
                  <TableRow className="hover:bg-transparent border-b border-[var(--line-warm)]">
                    <TableCell colSpan={visibleColCount} className="p-0">
                      <POExpandedDetail poId={poId} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </div>
    </ExpandContext.Provider>
  )
}
